import { Fragment, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetStockDayClose,
  useGetStockDayCloseOverdue,
  useSaveStockDayCloseCounts,
  useCloseStockDay,
  useReopenStockDay,
  getGetStockDayCloseQueryKey,
  getGetStockDayCloseOverdueQueryKey,
  type StockDayPositionLine,
  type StockDayItemRollup,
} from "@workspace/api-client-react";
import { AlertTriangle, ChevronDown, ChevronRight, Lock, RotateCcw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

// Phase 58 Plan 13 (DAYPOS-01/02/03) — the sheet-parity Day Close screen. The
// operator's own "ITEM MASTER & STOCK POSITION" sheet carries, per date block:
// Open Stock / Close Stock / Overflow-Underflow / Consumption (Qty) / Txn
// Amount / Profit, plus a TOTAL (ALL ITEMS) row. This screen has to read like
// that sheet or the operator keeps both open — so the column headings and the
// "Overflow/Underflow" wording below are load-bearing, not decoration.
//
// LOCKED: Open Stock is derived (prior close, or the live balance on day
// one) — never operator-typed. Overflow/Underflow is always COMPUTED from
// the typed Close, never entered directly, and saving a flagged row never
// requires a reason (variance is recorded always, flagged only when large).

const round2 = (n: number) => Math.round(n * 100) / 100;

const fmtDateInput = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function fmtQty(n: number | null): string {
  if (n == null) return "—";
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}
function fmtMoney(n: number): string {
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

interface EffectiveLine extends StockDayPositionLine {
  /** Server countedCloseQty, overridden by an in-progress (unsaved) typed value when present. */
  effectiveCountedCloseQty: number | null;
  /** effectiveCountedCloseQty - expectedCloseQty — recomputed on every keystroke, never typed. */
  effectiveVarianceQty: number | null;
}

// Override the generated type's optional-nullable fields as always-present
// (never undefined) — rollupLines() below always sets them to a number or an
// explicit null, so every consumer can rely on `number | null` without an
// extra `?? null` at every call site.
interface EffectiveRollup extends Omit<StockDayItemRollup, "countedCloseQty" | "varianceQty"> {
  countedCloseQty: number | null;
  varianceQty: number | null;
  allCounted: boolean;
}

function rollupLines(itemId: number, itemName: string, lines: EffectiveLine[]): EffectiveRollup {
  let openQty = 0,
    stockInQty = 0,
    recordedMovementQty = 0,
    billableConsumptionQty = 0,
    expectedCloseQty = 0,
    txnAmount = 0,
    profit = 0,
    countedCloseQty = 0,
    varianceQty = 0;
  let allCounted = true;
  let varianceFlagged = false;
  for (const l of lines) {
    openQty += l.openQty;
    stockInQty += l.stockInQty;
    recordedMovementQty += l.recordedMovementQty;
    billableConsumptionQty += l.billableConsumptionQty;
    expectedCloseQty += l.expectedCloseQty;
    txnAmount += l.txnAmount;
    profit += l.profit;
    varianceFlagged = varianceFlagged || l.varianceFlagged;
    if (l.effectiveCountedCloseQty == null) {
      allCounted = false;
    } else {
      countedCloseQty += l.effectiveCountedCloseQty;
      varianceQty += l.effectiveVarianceQty ?? 0;
    }
  }
  return {
    itemId,
    itemName,
    openQty: round2(openQty),
    stockInQty: round2(stockInQty),
    recordedMovementQty: round2(recordedMovementQty),
    billableConsumptionQty: round2(billableConsumptionQty),
    expectedCloseQty: round2(expectedCloseQty),
    countedCloseQty: allCounted ? round2(countedCloseQty) : null,
    varianceQty: allCounted ? round2(varianceQty) : null,
    varianceFlagged,
    txnAmount: round2(txnAmount),
    profit: round2(profit),
    allCounted,
  };
}

function pendingKey(itemId: number, locationId: number): string {
  return `${itemId}:${locationId}`;
}

export function StockDayCloseTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [date, setDate] = useState(() => fmtDateInput(new Date()));
  // itemId:locationId -> raw typed string. Cleared on date change and on a
  // successful save (the server response becomes the new source of truth).
  const [pending, setPending] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");

  useEffect(() => {
    setPending({});
  }, [date]);

  const sheetQuery = useGetStockDayClose(date, undefined, {
    query: { queryKey: getGetStockDayCloseQueryKey(date) },
  });
  const overdueQuery = useGetStockDayCloseOverdue();
  const sheet = sheetQuery.data;

  const linesByItem = useMemo(() => {
    const m = new Map<number, StockDayPositionLine[]>();
    for (const l of sheet?.lines ?? []) {
      const arr = m.get(l.itemId) ?? [];
      arr.push(l);
      m.set(l.itemId, arr);
    }
    return m;
  }, [sheet]);

  function toEffectiveLine(l: StockDayPositionLine): EffectiveLine {
    const key = pendingKey(l.itemId, l.locationId);
    const typed = pending[key];
    const counted = typed !== undefined ? (typed.trim() === "" ? null : Number(typed)) : l.countedCloseQty;
    const effectiveCountedCloseQty = counted != null && Number.isFinite(counted) ? counted : null;
    const effectiveVarianceQty =
      effectiveCountedCloseQty != null ? round2(effectiveCountedCloseQty - l.expectedCloseQty) : null;
    return { ...l, effectiveCountedCloseQty, effectiveVarianceQty };
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps -- toEffectiveLine closes over `pending`; listed explicitly below instead.
  const itemRows = useMemo(() => {
    const order = sheet?.rollup.map((r) => r.itemId) ?? [];
    return order.map((itemId) => {
      const rawLines = linesByItem.get(itemId) ?? [];
      const lines = rawLines.map(toEffectiveLine);
      const itemName = rawLines[0]?.itemName ?? `Item ${itemId}`;
      return { itemId, itemName, lines, rollup: rollupLines(itemId, itemName, lines) };
    });
  }, [sheet, linesByItem, pending]);

  const totalRow = useMemo(
    () => rollupLines(0, "TOTAL (ALL ITEMS)", itemRows.flatMap((r) => r.lines)),
    [itemRows],
  );

  const hasPending = Object.values(pending).some((v) => v.trim() !== "");
  const isClosed = sheet?.status === "closed";

  const saveMut = useSaveStockDayCloseCounts({
    mutation: {
      onSuccess: (data) => {
        qc.setQueryData(getGetStockDayCloseQueryKey(date), data);
        setPending({});
        toast({ title: "Counts saved.", description: "Overflow/Underflow recomputed from your entries." });
      },
      onError: (err: unknown) => {
        toast({
          title: "Couldn't save counts.",
          description: err instanceof Error ? err.message : undefined,
          variant: "destructive",
        });
      },
    },
  });

  const closeMut = useCloseStockDay({
    mutation: {
      onSuccess: (data) => {
        qc.setQueryData(getGetStockDayCloseQueryKey(date), data);
        qc.invalidateQueries({ queryKey: getGetStockDayCloseOverdueQueryKey() });
        toast({ title: `Day closed for ${date}.` });
      },
      onError: (err: unknown) => {
        toast({
          title: "Couldn't close the day.",
          description: err instanceof Error ? err.message : undefined,
          variant: "destructive",
        });
      },
    },
  });

  const reopenMut = useReopenStockDay({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getGetStockDayCloseQueryKey(date) });
        qc.invalidateQueries({ queryKey: getGetStockDayCloseOverdueQueryKey() });
        setReopenOpen(false);
        setReopenReason("");
        toast({ title: "Day reopened." });
      },
      onError: (err: unknown) => {
        toast({
          title: "Couldn't reopen.",
          description: err instanceof Error ? err.message : "A reason is required.",
          variant: "destructive",
        });
      },
    },
  });

  function onSaveCounts() {
    const counts = Object.entries(pending)
      .map(([key, v]) => {
        if (v.trim() === "") return null;
        const [itemIdStr, locationIdStr] = key.split(":");
        const n = Number(v);
        if (!Number.isFinite(n)) return null;
        return { itemId: Number(itemIdStr), locationId: Number(locationIdStr), countedCloseQty: n };
      })
      .filter((x): x is { itemId: number; locationId: number; countedCloseQty: number } => x != null);
    if (counts.length === 0) return;
    saveMut.mutate({ date, data: { counts } });
  }

  function onCloseDay() {
    if (hasPending) {
      toast({ title: "Save your counts before closing.", variant: "destructive" });
      return;
    }
    closeMut.mutate({ date, data: {} });
  }

  function onConfirmReopen() {
    if (!reopenReason.trim()) return;
    reopenMut.mutate({ date, data: { reason: reopenReason.trim() } });
  }

  function toggleExpand(itemId: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }

  const overdue = overdueQuery.data?.overdue ?? [];
  const todayStr = fmtDateInput(new Date());

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>Day Close</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Open Stock is carried from the prior close. Type only what you count for Close —
                Overflow/Underflow computes itself.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                aria-label="Business date"
                value={date}
                max={todayStr}
                onChange={(e) => setDate(e.target.value)}
                className="h-9 w-[160px]"
              />
              {sheet ? (
                <span
                  className={cn(
                    "rounded-full px-2.5 py-1 text-xs font-medium",
                    isClosed ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800",
                  )}
                >
                  {isClosed ? "Closed" : "Open"}
                </span>
              ) : null}
            </div>
          </div>

          {overdue.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2">
              <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0" />
              <span className="text-xs font-medium text-amber-900">Overdue days — not closed:</span>
              {overdue.map((o) => (
                <button
                  key={o.businessDate}
                  type="button"
                  onClick={() => setDate(o.businessDate)}
                  className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 underline hover:bg-amber-200"
                >
                  {o.businessDate}
                  {!o.hasRow ? " (missed)" : ""}
                </button>
              ))}
            </div>
          )}

          {sheet ? (
            <div
              className={cn(
                "mt-3 rounded-md border px-3 py-2 text-xs",
                sheet.isFullCount
                  ? "border-blue-300 bg-blue-50 text-blue-900"
                  : "border-muted bg-muted/40 text-muted-foreground",
              )}
            >
              {sheet.isFullCount
                ? "Full-count day — reserve locations (Store Room etc.) join the daily sale-facing locations below."
                : "Normal day — only sale-facing locations (Fridge, Pantry) are counted. Reserve locations appear on the next full-count day."}
            </div>
          ) : null}
        </CardHeader>

        <CardContent>
          {sheetQuery.isLoading ? (
            <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>
          ) : !sheet ? (
            <div className="py-10 text-center text-sm text-muted-foreground">Couldn't load this day.</div>
          ) : (
            <TooltipProvider>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8" />
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Open Stock</TableHead>
                      <TableHead className="text-right">Close Stock</TableHead>
                      <TableHead className="text-right">Overflow/Underflow</TableHead>
                      <TableHead className="text-right">Consumption (Qty)</TableHead>
                      <TableHead className="text-right">Txn Amount</TableHead>
                      <TableHead className="text-right">Profit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* TOTAL (ALL ITEMS) — pinned at the top, matching the sheet. */}
                    <TableRow className="bg-muted/60 font-semibold">
                      <TableCell />
                      <TableCell>TOTAL (ALL ITEMS)</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{fmtQty(totalRow.openQty)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">
                        {fmtQty(totalRow.countedCloseQty)}
                      </TableCell>
                      <VarianceCell value={totalRow.varianceQty} flagged={totalRow.varianceFlagged} />
                      <TableCell className="text-right font-mono tabular-nums">
                        {fmtQty(totalRow.billableConsumptionQty)}
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{fmtMoney(totalRow.txnAmount)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{fmtMoney(totalRow.profit)}</TableCell>
                    </TableRow>

                    {itemRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">
                          Nothing moved on this day yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      itemRows.map(({ itemId, itemName, lines, rollup }) => {
                        const isOpen = expanded.has(itemId);
                        return (
                          <Fragment key={itemId}>
                            <TableRow className="cursor-pointer hover:bg-muted/40" onClick={() => toggleExpand(itemId)}>
                              <TableCell>
                                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              </TableCell>
                              <TableCell className="text-sm font-medium">{itemName}</TableCell>
                              <TableCell className="text-right font-mono tabular-nums">{fmtQty(rollup.openQty)}</TableCell>
                              <TableCell className="text-right font-mono tabular-nums">
                                {fmtQty(rollup.countedCloseQty)}
                              </TableCell>
                              <VarianceCell value={rollup.varianceQty} flagged={rollup.varianceFlagged} />
                              <TableCell className="text-right font-mono tabular-nums">
                                {fmtQty(rollup.billableConsumptionQty)}
                              </TableCell>
                              <TableCell className="text-right font-mono tabular-nums">{fmtMoney(rollup.txnAmount)}</TableCell>
                              <TableCell className="text-right font-mono tabular-nums">{fmtMoney(rollup.profit)}</TableCell>
                            </TableRow>
                            {isOpen &&
                              lines.map((l) => (
                                <TableRow key={l.id} className="bg-muted/20">
                                  <TableCell />
                                  <TableCell className="pl-6 text-xs text-muted-foreground">{l.locationName}</TableCell>
                                  <TableCell className="text-right">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="cursor-help font-mono text-xs tabular-nums text-muted-foreground underline decoration-dotted">
                                          {fmtQty(l.openQty)}
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        {l.openBasis === "balance_fallback"
                                          ? "From the current stock balance — first counted day for this pair."
                                          : "Carried from the prior close."}
                                      </TooltipContent>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min={0}
                                      disabled={isClosed}
                                      aria-label={`Counted close for ${l.itemName} at ${l.locationName}`}
                                      className="ml-auto h-8 w-24 text-right font-mono tabular-nums"
                                      value={
                                        pending[pendingKey(l.itemId, l.locationId)] ??
                                        (l.countedCloseQty != null ? String(l.countedCloseQty) : "")
                                      }
                                      onChange={(e) => {
                                        const key = pendingKey(l.itemId, l.locationId);
                                        setPending((prev) => ({ ...prev, [key]: e.target.value }));
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </TableCell>
                                  <VarianceCell value={l.effectiveVarianceQty} flagged={l.varianceFlagged} />
                                  <TableCell className="text-right font-mono text-xs tabular-nums text-muted-foreground">
                                    {fmtQty(l.billableConsumptionQty)}
                                  </TableCell>
                                  <TableCell className="text-right font-mono text-xs tabular-nums text-muted-foreground">
                                    {fmtMoney(l.txnAmount)}
                                  </TableCell>
                                  <TableCell className="text-right font-mono text-xs tabular-nums text-muted-foreground">
                                    {fmtMoney(l.profit)}
                                  </TableCell>
                                </TableRow>
                              ))}
                          </Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </TooltipProvider>
          )}

          {sheet ? (
            <div className="mt-4 flex justify-end gap-2">
              {isClosed ? (
                <Button variant="outline" onClick={() => setReopenOpen(true)}>
                  <RotateCcw className="mr-2 h-4 w-4" /> Reopen
                </Button>
              ) : (
                <>
                  <Button variant="secondary" onClick={onSaveCounts} disabled={!hasPending || saveMut.isPending}>
                    {saveMut.isPending ? "Saving…" : "Save counts"}
                  </Button>
                  <Button onClick={onCloseDay} disabled={closeMut.isPending}>
                    <Lock className="mr-2 h-4 w-4" />
                    {closeMut.isPending ? "Closing…" : "Close day"}
                  </Button>
                </>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <AlertDialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reopen {date}?</AlertDialogTitle>
            <AlertDialogDescription>
              Reopening lets counts be edited again. A reason is required and is recorded on the audit trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {/* Plain markup, not FormField/FormLabel — this dialog has no react-hook-form
              context and FormLabel throws without a FormField ancestor. */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Reason</label>
            <Textarea
              placeholder="Why is this day being reopened?"
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setReopenReason("")}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirmReopen} disabled={!reopenReason.trim() || reopenMut.isPending}>
              {reopenMut.isPending ? "Reopening…" : "Reopen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function VarianceCell({ value, flagged }: { value: number | null; flagged: boolean }) {
  if (value == null) {
    return <TableCell className="text-right font-mono tabular-nums text-muted-foreground">—</TableCell>;
  }
  const isZero = value === 0;
  return (
    <TableCell
      className={cn(
        "text-right font-mono tabular-nums",
        !isZero && flagged ? "bg-red-50 font-semibold text-red-700" : !isZero ? "text-amber-700" : "text-muted-foreground",
      )}
    >
      {value > 0 ? "+" : ""}
      {fmtQty(value)}
      {flagged && !isZero ? <AlertTriangle className="ml-1 inline h-3 w-3" /> : null}
    </TableCell>
  );
}
